import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Search, 
  Filter, 
  Plus,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Truck,
  Home
} from "lucide-react";
import { OrdersTable } from "./OrdersTable";
import { OrderForm } from "./OrderForm";
import { useOrders } from "@/hooks/useOrders";
import { usePaymentStatuses } from "@/hooks/usePaymentStatuses";
import { useArtistDetails } from "@/hooks/useArtistDetails";

export function OrderManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>("success");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showOrderForm, setShowOrderForm] = useState(false);
  const { orders, loading, error, fetchOrders, createOrder, updateOrder, deleteOrder } = useOrders();
  const { paymentStatuses, loading: paymentStatusesLoading } = usePaymentStatuses();
  const { artistDetails } = useArtistDetails();

  useEffect(() => {
    console.log("🔄 OrderManagement component mounted, fetching orders...");
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    console.log("📊 Orders state updated:", { orders, loading, error });
  }, [orders, loading, error]);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.customer_first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.product_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || order.order_status === statusFilter;
    const matchesPaymentStatus = paymentStatusFilter === "all" || order.payment_status === paymentStatusFilter;
    
    let matchesDate = true;
    if (dateFilter === "custom" && startDate && endDate) {
      const orderDate = new Date(order.order_date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59); // Include the entire end date
      matchesDate = orderDate >= start && orderDate <= end;
    } else if (dateFilter === "today") {
      const today = new Date();
      const orderDate = new Date(order.order_date);
      matchesDate = orderDate.toDateString() === today.toDateString();
    } else if (dateFilter === "week") {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const orderDate = new Date(order.order_date);
      matchesDate = orderDate >= weekAgo;
    } else if (dateFilter === "month") {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      const orderDate = new Date(order.order_date);
      matchesDate = orderDate >= monthAgo;
    }
    
    return matchesSearch && matchesStatus && matchesPaymentStatus && matchesDate;
  });

  console.log("🔍 Filtered orders:", filteredOrders);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending_payment":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "payment_confirmed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "order_assigned":
        return <Package className="h-4 w-4 text-blue-500" />;
      case "design_production":
        return <Package className="h-4 w-4 text-indigo-500" />;
      case "quality_check":
        return <Package className="h-4 w-4 text-purple-500" />;
      case "ready_dispatch":
        return <Package className="h-4 w-4 text-orange-500" />;
      case "shipped":
        return <Truck className="h-4 w-4 text-blue-600" />;
      case "delivered":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-700" />;
      case "cancelled_refunded":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending_payment: "secondary",
      payment_confirmed: "default",
      order_assigned: "default",
      design_production: "default",
      quality_check: "default",
      ready_dispatch: "default",
      shipped: "default",
      delivered: "default",
      completed: "default",
      cancelled_refunded: "destructive"
    };

    const statusLabels: Record<string, string> = {
      pending_payment: "Pending Payment",
      payment_confirmed: "Payment Confirmed",
      order_assigned: "Order Assigned",
      design_production: "Design/Production",
      quality_check: "Quality Check",
      ready_dispatch: "Ready for Dispatch",
      shipped: "Shipped",
      delivered: "Delivered",
      completed: "Completed",
      cancelled_refunded: "Cancelled/Refunded"
    };

    return (
      <Badge variant={variants[status] || "outline"}>
        {getStatusIcon(status)}
        <span className="ml-1">{statusLabels[status] || status}</span>
      </Badge>
    );
  };

  const handleCreateOrder = async (orderData: any) => {
    try {
      // Generate a unique product ID if not provided
      if (!orderData.product_id) {
        orderData.product_id = `PROD-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      }
      
      await createOrder(orderData);
      setShowOrderForm(false);
      fetchOrders();
    } catch (error) {
      console.error("Error creating order:", error);
    }
  };

  const handleUpdateOrder = async (orderId: string, orderData: any) => {
    try {
      await updateOrder(orderId, orderData);
      fetchOrders();
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    try {
      await deleteOrder(orderId);
      fetchOrders();
    } catch (error) {
      console.error("Error deleting order:", error);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setPaymentStatusFilter("success");
    setDateFilter("all");
    setStartDate("");
    setEndDate("");
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Get filtered orders count by status
  const getFilteredCountByStatus = (status: string) => {
    return filteredOrders.filter(o => o.order_status === status).length;
  };

  // Get filtered orders count by payment status
  const getFilteredCountByPaymentStatus = (status: string) => {
    return filteredOrders.filter(o => o.payment_status === status).length;
  };

  if (loading || paymentStatusesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-red-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-4" />
            <p>Error loading orders: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Order Management</h1>
          <p className="text-muted-foreground">
            Manage customer orders and track their status
          </p>
          <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
            <span>Total Orders: {orders.length}</span>
            <span>Filtered: {filteredOrders.length}</span>
            {filteredOrders.length !== orders.length && (
              <span className="text-blue-600 font-medium">
                Showing {filteredOrders.length} of {orders.length} orders
              </span>
            )}
          </div>
        </div>
        <Button onClick={() => setShowOrderForm(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Order
        </Button>
      </div>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Orders ({filteredOrders.length})</TabsTrigger>
          <TabsTrigger value="pending_payment">
            Pending Payment ({orders.filter(o => o.order_status === "pending_payment").length})
          </TabsTrigger>
          <TabsTrigger value="payment_confirmed">
            Payment Confirmed ({orders.filter(o => o.order_status === "payment_confirmed").length})
          </TabsTrigger>
          <TabsTrigger value="order_assigned">
            Order Assigned ({orders.filter(o => o.order_status === "order_assigned").length})
          </TabsTrigger>
          <TabsTrigger value="design_production">
            Design/Production ({orders.filter(o => o.order_status === "design_production").length})
          </TabsTrigger>
          <TabsTrigger value="quality_check">
            Quality Check ({orders.filter(o => o.order_status === "quality_check").length})
          </TabsTrigger>
          <TabsTrigger value="ready_dispatch">
            Ready for Dispatch ({orders.filter(o => o.order_status === "ready_dispatch").length})
          </TabsTrigger>
          <TabsTrigger value="shipped">
            Shipped ({orders.filter(o => o.order_status === "shipped").length})
          </TabsTrigger>
          <TabsTrigger value="delivered">
            Delivered ({orders.filter(o => o.order_status === "delivered").length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed ({orders.filter(o => o.order_status === "completed").length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{orders.length}</div>
                <p className="text-xs text-muted-foreground">
                  {filteredOrders.length === orders.length ? "All orders" : `${filteredOrders.length} filtered`}
                </p>
              </CardContent>
            </Card>
                         <Card>
               <CardHeader className="pb-2">
                 <CardTitle className="text-sm font-medium">Pending Payment</CardTitle>
               </CardHeader>
               <CardContent>
                 <div className="text-2xl font-bold text-yellow-600">
                   {getFilteredCountByStatus("pending_payment")}
                 </div>
                 <p className="text-xs text-muted-foreground">Awaiting payment</p>
               </CardContent>
             </Card>
             <Card>
               <CardHeader className="pb-2">
                 <CardTitle className="text-sm font-medium">Payment Confirmed</CardTitle>
               </CardHeader>
               <CardContent>
                 <div className="text-2xl font-bold text-green-600">
                   {getFilteredCountByStatus("payment_confirmed")}
                 </div>
                 <p className="text-xs text-muted-foreground">Payment received</p>
               </CardContent>
             </Card>
             <Card>
               <CardHeader className="pb-2">
                 <CardTitle className="text-sm font-medium">In Production</CardTitle>
               </CardHeader>
               <CardContent>
                 <div className="text-2xl font-bold text-blue-600">
                   {getFilteredCountByStatus("design_production") + getFilteredCountByStatus("quality_check")}
                 </div>
                 <p className="text-xs text-muted-foreground">Design & production</p>
               </CardContent>
             </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Today's Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">
                  {orders.filter(o => {
                    const today = new Date();
                    const orderDate = new Date(o.order_date);
                    return orderDate.toDateString() === today.toDateString();
                  }).length}
                </div>
                <p className="text-xs text-muted-foreground">Orders placed today</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Orders Overview</CardTitle>
              <CardDescription>
                View and manage all customer orders
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search and Basic Filters */}
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search orders by customer name, email, or product..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Order Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Order Statuses</SelectItem>
                    <SelectItem value="pending_payment">Pending Payment</SelectItem>
                    <SelectItem value="payment_confirmed">Payment Confirmed</SelectItem>
                    <SelectItem value="order_assigned">Order Assigned to Artist/Seller</SelectItem>
                    <SelectItem value="design_production">Design/Production in Progress</SelectItem>
                    <SelectItem value="quality_check">Quality Check</SelectItem>
                    <SelectItem value="ready_dispatch">Ready for Dispatch</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled_refunded">Cancelled/Refunded</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter} disabled={paymentStatusesLoading}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder={paymentStatusesLoading ? "Loading..." : "Payment Status"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Payment Statuses</SelectItem>
                    {paymentStatusesLoading ? (
                      <SelectItem value="" disabled>Loading payment statuses...</SelectItem>
                    ) : (
                      paymentStatuses.map((status) => (
                        <SelectItem key={status.id} value={status.name}>
                          {status.name.charAt(0).toUpperCase() + status.name.slice(1)}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Date Filters */}
              <div className="flex gap-4 items-end">
                <div className="flex gap-2">
                  <Select value={dateFilter} onValueChange={setDateFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Date Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Dates</SelectItem>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="week">Last 7 Days</SelectItem>
                      <SelectItem value="month">Last 30 Days</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {dateFilter === "custom" && (
                    <>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        placeholder="Start Date"
                        className="w-40"
                      />
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        placeholder="End Date"
                        className="w-40"
                      />
                    </>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  onClick={clearAllFilters}
                  className="ml-auto"
                >
                  Clear Filters
                </Button>
              </div>

                             {/* Quick Filter Buttons */}
               <div className="flex flex-wrap gap-2">
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("pending_payment");
                     setPaymentStatusFilter(paymentStatuses.find(s => s.name === "pending")?.name || "pending");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                   disabled={paymentStatusesLoading}
                 >
                   Pending Payment
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("payment_confirmed");
                     setPaymentStatusFilter(paymentStatuses.find(s => s.name === "completed")?.name || "completed");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                   disabled={paymentStatusesLoading}
                 >
                   Payment Confirmed
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("order_assigned");
                     setPaymentStatusFilter("all");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                 >
                   Order Assigned
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("design_production");
                     setPaymentStatusFilter("all");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                 >
                   Design/Production
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("quality_check");
                     setPaymentStatusFilter("all");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                 >
                   Quality Check
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("ready_dispatch");
                     setPaymentStatusFilter("all");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                 >
                   Ready for Dispatch
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("shipped");
                     setPaymentStatusFilter("all");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                 >
                   Shipped Orders
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("delivered");
                     setPaymentStatusFilter("all");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                 >
                   Delivered Orders
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("completed");
                     setPaymentStatusFilter("all");
                     setDateFilter("all");
                   }}
                   className="text-xs"
                 >
                   Completed Orders
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("all");
                     setPaymentStatusFilter("all");
                     setDateFilter("today");
                   }}
                   className="text-xs"
                 >
                   Today's Orders
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("all");
                     setPaymentStatusFilter("all");
                     setDateFilter("week");
                   }}
                   className="text-xs"
                 >
                   This Week
                 </Button>
                 <Button
                   variant="outline"
                   size="sm"
                   onClick={() => {
                     setStatusFilter("all");
                     setPaymentStatusFilter("all");
                     setDateFilter("month");
                   }}
                   className="text-xs"
                 >
                   This Month
                 </Button>
               </div>

                             {/* Filter Summary */}
               {(searchTerm || statusFilter !== "all" || paymentStatusFilter !== "all" || dateFilter !== "all") && (
                 <div className="flex flex-wrap gap-2 items-center text-sm text-muted-foreground">
                   <span>Active filters:</span>
                   {searchTerm && (
                     <Badge variant="secondary" className="text-xs">
                       Search: "{searchTerm}"
                     </Badge>
                   )}
                   {statusFilter !== "all" && (
                     <Badge variant="secondary" className="text-xs">
                       Order Status: {statusFilter}
                     </Badge>
                   )}
                   {paymentStatusFilter !== "all" && (
                     <Badge variant="secondary" className="text-xs">
                       Payment Status: {paymentStatusFilter}
                     </Badge>
                   )}
                   {dateFilter !== "all" && (
                     <Badge variant="secondary" className="text-xs">
                       Date: {dateFilter === "custom" ? `${startDate} to ${endDate}` : dateFilter}
                     </Badge>
                   )}
                 </div>
               )}
               
               {/* Payment Statuses Loading Indicator */}
               {paymentStatusesLoading && (
                 <div className="flex items-center gap-2 text-sm text-muted-foreground">
                   <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                   <span>Loading payment statuses...</span>
                 </div>
               )}

              <OrdersTable
                orders={filteredOrders}
                onUpdateOrder={handleUpdateOrder}
                onDeleteOrder={handleDeleteOrder}
                getStatusBadge={getStatusBadge}
                artistDetails={artistDetails}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {["pending_payment", "payment_confirmed", "order_assigned", "design_production", "quality_check", "ready_dispatch", "shipped", "delivered", "completed"].map((status) => (
          <TabsContent key={status} value={status} className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {getStatusIcon(status)}
                  {getStatusBadge(status).props.children[1].props.children} Orders
                </CardTitle>
                <CardDescription>
                  Orders with {getStatusBadge(status).props.children[1].props.children} status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <OrdersTable
                  orders={orders.filter(o => o.order_status === status)}
                  onUpdateOrder={handleUpdateOrder}
                  onDeleteOrder={handleDeleteOrder}
                  getStatusBadge={getStatusBadge}
                  artistDetails={artistDetails}
                />
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {showOrderForm && (
        <OrderForm
          onSubmit={handleCreateOrder}
          onCancel={() => setShowOrderForm(false)}
        />
      )}
    </div>
  );
} 